import React from 'react';
import { 
  LayoutDashboard, 
  Phone, 
  BarChart3, 
  User, 
  Settings,
  LogOut,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDoctorData } from '../../context/DoctorDataContext';
import GlobalTimezoneSelector from './GlobalTimezoneSelector';

type DoctorViewType = 'dashboard' | 'requests' | 'analytics' | 'profile';

interface DoctorLayoutProps {
  currentView: DoctorViewType;
  onViewChange: (view: DoctorViewType) => void;
  doctorName: string;
  onBackToPatient: () => void;
  children: React.ReactNode;
}

const DoctorLayout: React.FC<DoctorLayoutProps> = ({ 
  currentView, 
  onViewChange, 
  doctorName,
  onBackToPatient,
  children 
}) => {
  const { logout, user } = useAuth();
  const { stats } = useDoctorData();

  const handleLogout = () => {
    logout();
    // Redirect to login page after logout
    window.location.href = '/login';
  };

  const navigationItems = [
    { id: 'dashboard' as DoctorViewType, label: 'Marketplace', icon: LayoutDashboard },
    { id: 'requests' as DoctorViewType, label: 'My Consultations', icon: Phone, badge: stats.totalActive + stats.totalCompleted },
    { id: 'analytics' as DoctorViewType, label: 'Analytics', icon: BarChart3 },
    { id: 'profile' as DoctorViewType, label: 'Profile', icon: User },
  ];

  return (
    <div className="min-h-screen bg-luma-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBackToPatient}
              className="p-2 text-gray-500 hover:text-luma-primary transition-colors rounded-lg hover:bg-gray-100"
              title="Back to Patient Portal"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="w-8 h-8 bg-luma-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-luma-text">SheCare</h1>
              <p className="text-sm text-gray-500">Doctor Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Global Timezone Selector */}
            <GlobalTimezoneSelector />
            
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-luma-text">{user?.name || doctorName}</p>
                <p className="text-xs text-gray-500">Online</p>
              </div>
              <div className="w-8 h-8 bg-luma-primary rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {(user?.name || doctorName).charAt(0)}
                </span>
              </div>
            </div>
            
            <button className="p-2 text-gray-500 hover:text-luma-primary transition-colors">
              <Settings className="w-5 h-5" />
            </button>
            
            <button 
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-red-500 transition-colors"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <nav className="p-4">
            <ul className="space-y-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => onViewChange(item.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-all ${
                        isActive
                          ? 'bg-luma-primary text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      
                      {item.badge && item.badge > 0 && (
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          isActive 
                            ? 'bg-white text-luma-primary' 
                            : 'bg-red-500 text-white'
                        }`}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DoctorLayout;